import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, Truck, Eye, X, AlertCircle, CheckCircle } from "lucide-react";
import toast from "react-hot-toast";
import { loginDeliveryPersonForDay } from "@/services/deliveryAgent";
import { startLocationTracking } from "@/lib/locationTracker";
import { storage } from "@/lib/storage";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { colors } from "@/theme";
import WebcamCapture, { type CapturedPhoto } from "./WebcamCapture";

export default function DeliveryShiftStart() {
  const navigate = useNavigate();
  const userDetails = useCurrentUser();
  const userData = storage.getUserData();
  const username =
    userDetails?.username || userData?.user?.username || userData?.username || "";

  const [showCamera, setShowCamera] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState<CapturedPhoto | null>(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handlePhotoCaptured = useCallback((photo: CapturedPhoto) => {
    setCapturedPhoto(photo);
    setShowCamera(false);
    setError("");
  }, []);

  const handleStartDay = useCallback(async () => {
    if (!capturedPhoto) return;
    setLoading(true);
    setError("");
    try {
      const formData = new FormData();
      formData.append("Username", username);
      formData.append("Image", capturedPhoto.blob, "login.jpg");
      const response = await loginDeliveryPersonForDay(formData);
      if (response?.status === 200 || response?.status === 201) {
        setSuccess(true);
        toast.success("Shift started successfully!");
        startLocationTracking(userDetails);
        setTimeout(() => navigate("/delivery/dashboard"), 2000);
      } else {
        const msg =
          (response?.data as any)?.message ||
          (response?.data as any)?.detail ||
          "Unable to start your shift. Please try again.";
        setError(msg);
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [capturedPhoto, username, navigate]);

  // ── Success state ────────────────────────────────────────────────────────
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: colors.brand.gradient }}>
        <motion.div
          initial={{ scale: 0.7, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 200, damping: 20 }}
          className="text-center"
        >
          <div className="w-24 h-24 rounded-full bg-white/20 border-2 border-white/30 flex items-center justify-center mx-auto mb-6 shadow-2xl">
            <CheckCircle className="w-12 h-12 text-white" />
          </div>
          <h2 className="text-3xl font-extrabold text-white mb-2">Shift Started!</h2>
          <p className="text-white/75 text-sm">Redirecting to dashboard…</p>
        </motion.div>
      </div>
    );
  }

  return (
    <>
      {/* ── Webcam Modal ─────────────────────────────────────────────────────── */}
      <WebcamCapture
        open={showCamera}
        onCapture={handlePhotoCaptured}
        onCancel={() => setShowCamera(false)}
        // hint="Position your face in the circle"
        initialFacing="user"
      />

      {/* ── Full-image preview modal ─────────────────────────────────────────── */}
      <AnimatePresence>
        {previewModalOpen && capturedPhoto && (
          <motion.div
            key="img-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/92 flex items-center justify-center px-4"
            onClick={() => setPreviewModalOpen(false)}
          >
            <button
              onClick={() => setPreviewModalOpen(false)}
              className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white/15 flex items-center justify-center text-white hover:bg-white/25 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <motion.img
              initial={{ scale: 0.88, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              src={capturedPhoto.previewUrl}
              alt="Captured"
              className="max-w-md w-full rounded-2xl shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Main Screen ──────────────────────────────────────────────────────── */}
      <div
        className="min-h-screen flex flex-col items-center justify-center px-5 py-14 relative overflow-hidden"
        style={{ background: colors.brand.gradient }}
      >
        {/* Background blobs */}
        <div className="absolute -top-28 -right-28 w-80 h-80 rounded-full bg-white/[0.05] pointer-events-none" />
        <div className="absolute -bottom-36 -left-24 w-96 h-96 rounded-full bg-white/[0.04] pointer-events-none" />

        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
          className="w-full max-w-sm"
        >
          {/* Icon */}
          <motion.div
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.15, type: "spring", stiffness: 160 }}
            className="w-32 h-32 rounded-full border border-white/20 bg-white/10 flex items-center justify-center mx-auto mb-8 shadow-2xl"
          >
            <Truck className="w-14 h-14 text-white" />
          </motion.div>

          <h1 className="text-3xl font-extrabold text-white text-center tracking-tight mb-2">
            Start Your Shift
          </h1>
          <p className="text-white/60 text-sm text-center mb-10 leading-relaxed">
            Take a clear selfie to verify your identity and begin today's delivery duty.
          </p>

          {/* ── Captured photo box with eye icon ─────────────────────────────── */}
          <AnimatePresence>
            {capturedPhoto && (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="relative rounded-2xl overflow-hidden mb-5 border border-white/20 shadow-xl"
                style={{ height: 180 }}
              >
                <img
                  src={capturedPhoto.previewUrl}
                  alt="Shift selfie"
                  className="w-full h-full object-cover"
                />
                {/* Eye icon — top right */}
                <button
                  onClick={() => setPreviewModalOpen(true)}
                  className="absolute top-2.5 right-2.5 w-9 h-9 rounded-xl bg-black/55 border border-white/20 flex items-center justify-center text-white hover:bg-black/75 transition-colors backdrop-blur-sm"
                >
                  <Eye className="w-4 h-4" />
                </button>
                {/* Retake overlay at bottom */}
                <button
                  onClick={() => setShowCamera(true)}
                  className="absolute bottom-0 inset-x-0 py-2.5 bg-black/55 text-white text-xs font-semibold text-center flex items-center justify-center gap-1.5 hover:bg-black/70 transition-colors backdrop-blur-sm"
                >
                  <Camera className="w-3.5 h-3.5" />
                  Retake
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Error */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-5 bg-red-500/15 border border-red-400/30 rounded-xl px-4 py-3 flex items-start gap-2"
              >
                <AlertCircle className="w-4 h-4 text-red-300 mt-0.5 flex-shrink-0" />
                <p className="text-red-200 text-sm">{error}</p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* CTA */}
          {!capturedPhoto ? (
            <button
              onClick={() => setShowCamera(true)}
              className="w-full py-4 rounded-2xl bg-white font-bold text-sm flex items-center justify-center gap-2 shadow-xl transition-transform hover:scale-[1.02] active:scale-[0.98]"
              style={{ color: colors.brand.secondary }}
            >
              <Camera className="w-5 h-5" />
              Open Camera
            </button>
          ) : (
            <button
              onClick={handleStartDay}
              disabled={loading}
              className="w-full py-4 rounded-2xl bg-white font-bold text-sm flex items-center justify-center gap-2 shadow-xl transition-transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60 disabled:scale-100"
              style={{ color: colors.brand.secondary }}
            >
              {loading ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Starting Shift…
                </>
              ) : (
                <>
                  <Truck className="w-5 h-5" />
                  Start Your Day
                </>
              )}
            </button>
          )}

          {username && (
            <p className="mt-6 text-white/35 text-xs text-center">
              Logged in as <span className="text-white/65 font-semibold">{username}</span>
            </p>
          )}
        </motion.div>
      </div>
    </>
  );
}
